import {
  BadRequestError,
  FetchError,
  NotFoundError,
  UnauthorizedError
} from '@nankai/admin/views/errors';
import { t } from 'i18next';
import {
  getSessionItem,
  SessionStorageKeys,
  setSessionItem
} from '../utils/sessionStorage';
import { firebaseAuth } from '../vendors/firebase';
import { PASSWORD_RESET_PATH } from './apiPaths';

export const API_ENV = import.meta.env.VITE_APP_ADMIN_ENV;
export const API_TTRS_HOST = import.meta.env.VITE_API_TTRS_HOST as string;
export const IS_LOCAL = API_ENV === 'local';

/**
 * トークン不要なAPI一覧
 * - この配列に部分一致するURLは Authorization ヘッダーを付与しない
 */
const NO_AUTH_PATHS: string[] = [PASSWORD_RESET_PATH];

/**
 * 共通API呼び出し関数
 * - 認証付きAPIは自動的に Bearer token を付与
 * - NO_AUTH_PATHS に該当する場合はトークンなしでアクセス
 */
export async function fetchWithAuth<T = any>(
  path: string,
  options: RequestInit = {},
  responseType: 'json' | 'response' = 'json'
): Promise<T> {
  // local環境ではCORS回避のためViteのproxy経由でアクセスするのでAPI_HOSTは付けない
  const url = IS_LOCAL ? path : `${API_TTRS_HOST}${path}`;

  const isTokenRequired = !NO_AUTH_PATHS.some(p => path.includes(p));
  const idToken = getSessionItem<string>(SessionStorageKeys.ID_TOKEN);

  if (isTokenRequired && !idToken) {
    throw new UnauthorizedError(t('error.authRequired'));
  }

  try {
    const doFetch = async (token: string) => {
      return await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
    };

    let res = await doFetch(idToken || '');

    // 401時にFirebaseトークンをリフレッシュして再試行
    if (res.status === 401 && firebaseAuth.currentUser) {
      const freshToken = await firebaseAuth.currentUser.getIdToken(true);
      setSessionItem(SessionStorageKeys.ID_TOKEN, freshToken);
      res = await doFetch(freshToken);
    }

    // 再試行後も401なら認証エラー（セッション切れ or 無効トークン）
    if (res.status === 401) {
      throw new UnauthorizedError(t('error.authExpired'));
    }

    if (res.ok && responseType === 'response') {
      return res as T;
    }

    const result = await res.json().catch(() => null);

    // ステータスごとのエラーハンドリング
    if (!res.ok) {
      console.error(`fetchWithAuth failed: ${res.url} ${res.status}`);

      if (res.status === 400 || res.status === 409) {
        throw new BadRequestError(result?.message || res.statusText);
      } else if (res.status === 404) {
        throw new NotFoundError(t('error.dataNotFoundError'));
      } else {
        throw new FetchError(res, t('error.systemError'));
      }
    }

    return result as T;
  } catch (e) {
    console.error('[fetchWithAuth] error:', e);
    throw e;
  }
}
